import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: block;
  pointer-events: ${(props) => (props.$thumbnail ? 'none' : 'initial')};
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio (9 / 16 * 100%) */
  height: 0;
  overflow: hidden;
  max-width: 100%;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

/**
 * YouTubePreview component to render a product preview video
 *
 * @param {object} props - The props object
 * @param {string} props.url - The video URL
 * @param {boolean} props.thumbnail - The video URL
 * @returns {JSX.Element} The rendered component
 */
const YouTubePreview = ({ url, thumbnail }) => {
  const [videoID, setVideoID] = useState(null);

  /**
   * Extract the YouTube video ID from the URL
   *
   * @param {string} videoUrl - The YouTube video URL
   * @returns {string|null} The extracted video ID or null if not found
   */
  const getYouTubeVideoID = (videoUrl) => {
    const regex =
      /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.+[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
    const match = videoUrl.match(regex);
    return match && match[1] ? match[1] : null;
  };

  useEffect(() => {
    const id = getYouTubeVideoID(url);
    setVideoID(id);
  }, [url]);

  if (!videoID) {
    return <div>Invalid YouTube URL</div>;
  }

  return (
    <Container $thumbnail={thumbnail}>
      {thumbnail ? (
        <iframe
          width="100%"
          src={`https://www.youtube.com/embed/${videoID}?controls=0&mute=1&loop=1&color=white&modestbranding=0&playsinline=1&rel=0&enablejsapi=1`}
          title="YouTube video player"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen={false}
          id="videoID"
          type="text/html"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      ) : (
        <iframe
          width="100%"
          src={`https://www.youtube.com/embed/${videoID}`}
          title="YouTube video player"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          id="videoID"
          type="text/html"
        />
      )}
    </Container>
  );
};

YouTubePreview.propTypes = {
  url: PropTypes.string.isRequired,
  thumbnail: PropTypes.bool,
};

YouTubePreview.defaultProps = {
  thumbnail: false,
};

export default YouTubePreview;
